// engine.js — Core projection simulation (pure functions, no React)
// Deterministic 10-year simulation for frontier T-bill portfolios

import { calcDiscountPrice, calcNetYield } from '../dealing.js';
import {
  MARKET_CONFIG, BENCHMARK_CONFIG, COST_DEFAULTS,
  PROJECTION_HORIZON, YIELD_MEAN_REVERSION_SPEED,
  ADVERSE_YIELD_SPIKE_BPS, ADVERSE_SHOCK_YEAR,
  DEFAULT_REINVESTMENT_TENOR, DEFAULT_HEDGE_RATIO,
} from './constants.js';

// ─── Yield Path Derivation ────────────────────────────────────────────

/**
 * Derive annual yield path for a given market and scenario.
 * @param {number} currentYield - current annual yield (decimal)
 * @param {number} longRunYield - long-run equilibrium yield (decimal)
 * @param {'base'|'optimistic'|'adverse'} scenario
 * @param {number} horizon - number of years
 * @param {{ shockYear?: number }} adverseConfig
 * @returns {number[]} array of yields, length = horizon + 1 (year 0 through year N)
 */
export function deriveYieldPath(currentYield, longRunYield, scenario, horizon = PROJECTION_HORIZON, adverseConfig = {}) {
  const path = [currentYield];
  const shockYear = adverseConfig.shockYear ?? ADVERSE_SHOCK_YEAR;

  for (let t = 1; t <= horizon; t++) {
    const prev = path[t - 1];

    if (scenario === 'optimistic') {
      // Flat at current level
      path.push(currentYield);
    } else if (scenario === 'base') {
      // Exponential mean-reversion toward long-run
      const next = prev + YIELD_MEAN_REVERSION_SPEED * (longRunYield - prev);
      path.push(next);
    } else if (scenario === 'adverse') {
      // Mean-reversion with a spike in the shock year
      let next = prev + YIELD_MEAN_REVERSION_SPEED * (longRunYield - prev);
      if (t === shockYear) {
        next += ADVERSE_YIELD_SPIKE_BPS / 10000;
      }
      path.push(next);
    } else {
      // Custom: treat same as base
      const next = prev + YIELD_MEAN_REVERSION_SPEED * (longRunYield - prev);
      path.push(next);
    }
  }

  return path;
}

// ─── FX Path Derivation ──────────────────────────────────────────────

/**
 * Derive annual FX spot path using uncovered interest parity (UIP).
 * @param {number} currentSpot - current spot rate (local per base)
 * @param {number[]} localYieldPath - local yield path
 * @param {number[]} baseYieldPath - base-currency yield path
 * @param {'base'|'optimistic'|'adverse'} scenario
 * @param {{ shockYear?: number, historicalShock?: number }} adverseConfig
 * @returns {number[]} array of spot rates, length = horizon + 1
 */
export function deriveFxPath(currentSpot, localYieldPath, baseYieldPath, scenario, adverseConfig = {}) {
  const path = [currentSpot];
  const shockYear = adverseConfig.shockYear ?? ADVERSE_SHOCK_YEAR;
  const historicalShock = adverseConfig.historicalShock ?? -0.20;

  for (let t = 1; t < localYieldPath.length; t++) {
    const prevSpot = path[t - 1];

    if (scenario === 'optimistic') {
      // FX remains stable
      path.push(currentSpot);
    } else {
      // UIP: spot depreciates by local-base yield differential
      const localY = localYieldPath[t - 1];
      const baseY = baseYieldPath[t - 1];
      let nextSpot = prevSpot * (1 + localY) / (1 + baseY);

      // Adverse: apply historical devaluation shock
      if (scenario === 'adverse' && t === shockYear) {
        nextSpot = nextSpot * (1 - historicalShock); // historicalShock is negative, so this increases spot
      }

      path.push(nextSpot);
    }
  }

  return path;
}

// ─── Forward Rate (CIP) ─────────────────────────────────────────────

/**
 * Calculate 1-year forward rate via covered interest parity.
 * F = S x (1 + r_local) / (1 + r_base) + forward spread
 * @param {number} spot - current spot rate
 * @param {number} localYield - local annual yield
 * @param {number} baseYield - base-currency annual yield
 * @param {number} forwardSpreadBps - forward spread in bps
 * @returns {number} forward rate
 */
export function calcForwardRate(spot, localYield, baseYield, forwardSpreadBps = COST_DEFAULTS.forwardSpreadBps) {
  const baseForward = spot * (1 + localYield) / (1 + baseYield);
  const spreadAdjustment = spot * (forwardSpreadBps / 10000);
  return baseForward + spreadAdjustment;
}

// ─── Single Year Step ────────────────────────────────────────────────

/**
 * Simulate one year of portfolio evolution for a single market.
 * @param {Object} beginState - { value, valueBCY, market }
 * @param {number} yearIdx - 0-based year index
 * @param {Object} yields - { local: number, base: number }
 * @param {Object} fx - { spot: number, prevSpot: number }
 * @param {number} forwardRate
 * @param {Object} costs - COST_DEFAULTS shape
 * @param {string} mode - profit-taking mode key
 * @param {Object} modeParams - mode-specific params (e.g. { withdrawalPct })
 * @param {number} hedgeRatio - 0 to 1
 * @param {number} whtRate - withholding tax rate (decimal)
 * @param {number} tenorDays - reinvestment tenor
 * @returns {Object} yearResult
 */
export function stepOneYear(beginState, yearIdx, yields, fx, forwardRate, costs, mode, modeParams, hedgeRatio, whtRate, tenorDays) {
  const { value } = beginState; // value in local currency
  const localY = yields.local;
  const baseY = yields.base;

  // Gross income from T-bill discount
  const grossYield = localY;
  const netYield = calcNetYield(grossYield * 100, whtRate * 100, costs.platformFeeBps + costs.custodyBps) / 100;

  // Income earned (local)
  const grossIncome = value * grossYield;
  const whtAmount = grossIncome * whtRate;
  const feeAmount = value * ((costs.platformFeeBps + costs.custodyBps) / 10000);
  const netIncome = value * netYield;

  // Profit-taking / withdrawal
  let withdrawal = 0;
  if (mode === 'interest_only') {
    withdrawal = netIncome;
  } else if (mode === 'annual_withdraw') {
    const pct = (modeParams && modeParams.withdrawalPct) || 0.05;
    withdrawal = (value + netIncome) * pct;
  } else if (mode === 'amortise') {
    const remainingYears = PROJECTION_HORIZON - yearIdx;
    if (remainingYears > 0) {
      withdrawal = (value + netIncome) / remainingYears;
    }
  }
  // full_reinvest: withdrawal = 0

  const endValueLocal = value + grossIncome - whtAmount - feeAmount - withdrawal;

  // FX conversion to base currency
  const spot = fx.spot;
  const fxSpreadCost = value * (costs.fxSpreadBps / 10000) / spot;

  // Hedged value: hedge portion at forward rate, unhedged at spot
  const hedgedValueBCY = hedgeRatio * (endValueLocal / forwardRate) + (1 - hedgeRatio) * (endValueLocal / spot);
  const unhedgedValueBCY = endValueLocal / spot;

  // Forward cost (opportunity cost of hedging)
  const forwardCostBCY = hedgeRatio * (endValueLocal / spot - endValueLocal / forwardRate);

  // FX impact (change in base-currency value due to spot movement)
  const prevSpot = fx.prevSpot;
  const fxImpactBCY = prevSpot > 0 ? value * (1 / spot - 1 / prevSpot) : 0;

  return {
    year: yearIdx + 1,
    beginValueLocal: value,
    beginValueBCY: prevSpot > 0 ? value / prevSpot : 0,
    grossIncome,
    whtAmount,
    feeAmount,
    netIncome,
    withdrawal,
    endValueLocal,
    endValueBCYHedged: hedgedValueBCY - fxSpreadCost,
    endValueBCYUnhedged: unhedgedValueBCY - fxSpreadCost,
    fxImpactBCY,
    forwardCostBCY,
    fxSpreadCost,
    spot,
    forwardRate,
    localYield: localY,
    baseYield: baseY,
  };
}

// ─── Benchmark Step ──────────────────────────────────────────────────

/**
 * Step the benchmark forward one year.
 * @param {number} beginValue - benchmark value in base currency
 * @param {number} benchmarkYield - annual yield (decimal)
 * @param {number} withdrawal - cash withdrawn (base currency)
 * @returns {number} end value
 */
export function stepBenchmark(beginValue, benchmarkYield, withdrawal = 0) {
  return beginValue * (1 + benchmarkYield) - withdrawal;
}

// ─── Build Initial State ─────────────────────────────────────────────

/**
 * Build initial portfolio state from holdings, grouped by market.
 * @param {Object[]} positions - portfolio positions
 * @param {Object} currentFxRates - { [market]: rate } (local per base)
 * @returns {{ markets: Object, totalBCY: number }}
 */
export function buildInitialState(positions, currentFxRates = {}) {
  const markets = {};
  let totalBCY = 0;

  (positions || []).forEach(p => {
    const market = p.country;
    if (!markets[market]) {
      markets[market] = { market, valueLocal: 0, valueBCY: 0 };
    }
    markets[market].valueLocal += p.faceValue || 0;
    const fxRate = currentFxRates[market] || p.currentFxRate || 1;
    markets[market].valueBCY += (p.faceValue || 0) / fxRate;
  });

  Object.values(markets).forEach(m => { totalBCY += m.valueBCY; });

  return { markets, totalBCY };
}

// ─── Run Full Projection ─────────────────────────────────────────────

/**
 * Run the complete projection across all scenarios.
 * @param {Object} params
 * @param {Object[]} params.positions - portfolio positions
 * @param {Object} params.currentFxRates - { [market]: fxRate }
 * @param {Object} params.currentYields - { [market]: currentYield364 } (decimal)
 * @param {string} params.baseCurrency - 'USD', 'GBP', etc.
 * @param {string} params.mode - profit-taking mode
 * @param {Object} params.modeParams - mode-specific params
 * @param {number} params.hedgeRatio - 0-1
 * @param {Object} params.costs - COST_DEFAULTS shape
 * @param {number} params.tenorDays - reinvestment tenor
 * @param {string[]} params.scenarios - array of scenario keys
 * @param {number} params.adverseShockYear - 1-indexed shock year
 * @param {Object} params.customYieldOverrides - { [market]: yield } for custom scenario
 * @returns {Object} projectionResult
 */
export function runProjection(params) {
  const {
    positions = [],
    currentFxRates = {},
    currentYields = {},
    baseCurrency = 'USD',
    mode = 'full_reinvest',
    modeParams = {},
    hedgeRatio = DEFAULT_HEDGE_RATIO,
    costs = COST_DEFAULTS,
    tenorDays = DEFAULT_REINVESTMENT_TENOR,
    scenarios = ['base', 'optimistic', 'adverse'],
    adverseShockYear = ADVERSE_SHOCK_YEAR,
    customYieldOverrides = null,
  } = params;

  const horizon = PROJECTION_HORIZON;
  const benchmarkConfig = BENCHMARK_CONFIG[baseCurrency] || BENCHMARK_CONFIG.USD;
  const benchmarkYield = benchmarkConfig.yield;

  // Build initial state
  const initialState = buildInitialState(positions, currentFxRates);
  const initialValueBCY = initialState.totalBCY;

  // Get active markets
  const activeMarkets = Object.keys(initialState.markets);
  if (activeMarkets.length === 0) {
    return { scenarios: {}, benchmark: [], assumptions: [], meta: { initialValueBCY: 0, horizon } };
  }

  // Build base yield path for benchmark currency
  const baseYieldPath = Array(horizon + 1).fill(benchmarkYield);

  // Run each scenario
  const scenarioResults = {};

  scenarios.forEach(scenarioKey => {
    // Build per-market yield and FX paths
    const yieldPaths = {};
    const fxPaths = {};

    activeMarkets.forEach(market => {
      const mc = MARKET_CONFIG[market];
      if (!mc) return;

      const currentY = currentYields[market] || mc.longRunYield;
      const adverseConfig = {
        shockYear: adverseShockYear,
        historicalShock: mc.historicalShock,
      };

      yieldPaths[market] = deriveYieldPath(currentY, mc.longRunYield, scenarioKey, horizon, adverseConfig);

      const currentSpot = currentFxRates[market] || 1;
      fxPaths[market] = deriveFxPath(currentSpot, yieldPaths[market], baseYieldPath, scenarioKey, adverseConfig);
    });

    // Simulate year-by-year
    const years = [];
    const marketStates = {};

    // Initialize market states
    activeMarkets.forEach(market => {
      if (MARKET_CONFIG[market]) {
        marketStates[market] = {
          value: initialState.markets[market].valueLocal,
          valueBCY: initialState.markets[market].valueBCY,
          market,
        };
      }
    });

    let totalHedgedBCY = initialValueBCY;
    let totalUnhedgedBCY = initialValueBCY;

    for (let t = 0; t < horizon; t++) {
      let yearTotalHedged = 0;
      let yearTotalUnhedged = 0;
      let yearIncome = 0;
      let yearWithdrawal = 0;
      let yearFees = 0;
      let yearTaxes = 0;
      let yearFxImpact = 0;
      let yearForwardCost = 0;

      activeMarkets.forEach(market => {
        const mc = MARKET_CONFIG[market];
        if (!mc || !marketStates[market]) return;

        const localY = yieldPaths[market][t + 1];
        const baseY = baseYieldPath[t + 1];
        const spot = fxPaths[market][t + 1];
        const prevSpot = fxPaths[market][t];
        const fwd = calcForwardRate(spot, localY, baseY, costs.forwardSpreadBps);

        const yearResult = stepOneYear(
          marketStates[market], t,
          { local: localY, base: baseY },
          { spot, prevSpot },
          fwd, costs, mode, modeParams,
          hedgeRatio, mc.whtRate, tenorDays
        );

        // Update state for next year
        marketStates[market] = {
          value: yearResult.endValueLocal,
          valueBCY: yearResult.endValueBCYHedged,
          market,
        };

        yearTotalHedged += yearResult.endValueBCYHedged;
        yearTotalUnhedged += yearResult.endValueBCYUnhedged;
        yearIncome += yearResult.grossIncome / spot;
        yearWithdrawal += yearResult.withdrawal / spot;
        yearFees += yearResult.feeAmount / spot;
        yearTaxes += yearResult.whtAmount / spot;
        yearFxImpact += yearResult.fxImpactBCY;
        yearForwardCost += yearResult.forwardCostBCY;
      });

      years.push({
        year: t + 1,
        valueHedged: yearTotalHedged,
        valueUnhedged: yearTotalUnhedged,
        income: yearIncome,
        withdrawal: yearWithdrawal,
        fees: yearFees,
        taxes: yearTaxes,
        fxImpact: yearFxImpact,
        forwardCost: yearForwardCost,
      });

      totalHedgedBCY = yearTotalHedged;
      totalUnhedgedBCY = yearTotalUnhedged;
    }

    // Add year 0
    years.unshift({
      year: 0,
      valueHedged: initialValueBCY,
      valueUnhedged: initialValueBCY,
      income: 0,
      withdrawal: 0,
      fees: 0,
      taxes: 0,
      fxImpact: 0,
      forwardCost: 0,
    });

    // Calculate annualized return
    const endHedged = totalHedgedBCY;
    const annualizedHedged = initialValueBCY > 0
      ? Math.pow(endHedged / initialValueBCY, 1 / horizon) - 1
      : 0;
    const annualizedUnhedged = initialValueBCY > 0
      ? Math.pow(totalUnhedgedBCY / initialValueBCY, 1 / horizon) - 1
      : 0;

    scenarioResults[scenarioKey] = {
      years,
      endValueHedged: endHedged,
      endValueUnhedged: totalUnhedgedBCY,
      annualizedReturnHedged: annualizedHedged,
      annualizedReturnUnhedged: annualizedUnhedged,
      yieldPaths,
      fxPaths,
    };
  });

  // Run benchmark path
  const benchmarkYears = [{ year: 0, value: initialValueBCY }];
  let benchmarkValue = initialValueBCY;
  for (let t = 0; t < horizon; t++) {
    // Use base scenario withdrawals for benchmark comparison
    const baseWithdrawal = scenarioResults.base ? scenarioResults.base.years[t + 1].withdrawal : 0;
    benchmarkValue = stepBenchmark(benchmarkValue, benchmarkYield, baseWithdrawal);
    benchmarkYears.push({ year: t + 1, value: benchmarkValue });
  }

  const benchmarkAnnualized = initialValueBCY > 0
    ? Math.pow(benchmarkValue / initialValueBCY, 1 / horizon) - 1
    : 0;

  // Build assumptions digest
  const assumptions = buildAssumptionDigest(params, scenarioResults);

  return {
    scenarios: scenarioResults,
    benchmark: {
      years: benchmarkYears,
      endValue: benchmarkValue,
      annualizedReturn: benchmarkAnnualized,
      label: benchmarkConfig.label,
      yield: benchmarkYield,
    },
    assumptions,
    meta: {
      initialValueBCY,
      horizon,
      baseCurrency,
      mode,
      hedgeRatio,
      tenorDays,
      costs,
      activeMarkets,
    },
  };
}

// ─── Assumptions Digest ──────────────────────────────────────────────

/**
 * Build a plain-language list of all assumptions.
 * @param {Object} params - original runProjection params
 * @param {Object} scenarioResults - computed scenario results
 * @returns {string[]} array of assumption strings
 */
export function buildAssumptionDigest(params, scenarioResults) {
  const {
    baseCurrency = 'USD',
    mode = 'full_reinvest',
    hedgeRatio = DEFAULT_HEDGE_RATIO,
    costs = COST_DEFAULTS,
    tenorDays = DEFAULT_REINVESTMENT_TENOR,
    adverseShockYear = ADVERSE_SHOCK_YEAR,
  } = params;

  const assumptions = [
    `Projection horizon: ${PROJECTION_HORIZON} years`,
    `Base currency: ${baseCurrency}`,
    `Reinvestment tenor: ${tenorDays} days`,
    `Hedge ratio: ${(hedgeRatio * 100).toFixed(0)}%`,
    `Platform fee: ${costs.platformFeeBps}bps; foreign exchange spread: ${costs.fxSpreadBps}bps; Forward spread: ${costs.forwardSpreadBps}bps; Custody: ${costs.custodyBps}bps`,
    `Profit-taking mode: ${mode.replace(/_/g, ' ')}`,
    `Yield mean-reversion speed: ${(YIELD_MEAN_REVERSION_SPEED * 100).toFixed(0)}% per year toward long-run`,
    `Adverse scenario: +${ADVERSE_YIELD_SPIKE_BPS}bps yield shock in year ${adverseShockYear}, historical foreign exchange devaluation, 3-month repatriation delay`,
    'This is an illustration, not a forecast.',
  ];

  return assumptions;
}
